import type React from 'react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient } from '../api';
import {
  IconEbene, IconRaum, IconSuche, IconOk, IconWarnung,
  IconSanitaer, IconElektro, IconLueftung,
} from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, FilterBar, LoadingState,
  type FilterDefinition, type FilterWerte, type Spalte,
} from '../muster';

const KOPFTEXT = 'Der Küchenbestand: Gliederung, Räume, Pläne und die Anschlusspunkte '
  + 'aus der Planauswertung. Führend ist hier die Datenbank — das Modell ist das Ziel.';

/**
 * Fremdmodelle > Küchenplanung.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DER AUFBAU FOLGT DER HEIZUNG — AUS EINEM GRUND
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Links die Gliederung, rechts der gewählte Knoten. Bei 6.674 Bauteilen ist
 * eine flache Liste nicht benutzbar, und ein Baum über die ganze Breite mit
 * dem Inhalt darunter zwingt zum Rollen zwischen Auswahl und Anzeige.
 *
 * Neu gegenüber der Heizung ist die vierte Stufe:
 *
 *     Ebene  →  Abschnitt  →  Raum  →  Liste der Komponenten
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DER BAUM ZEIGT DEN BESTAND — DIE PLANPOSITIONEN STEHEN DANEBEN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der Bestand sind die 6.674 Bauteile aus den beiden KUCA-Modellen. Die 845
 * Zeilen der alten Planauswertung sind seit Migration 0401 kein Bestand mehr;
 * sie haben ihren eigenen Reiter.
 *
 * BEFUND 20.08.2026: Beide Mengen standen vermischt in einer Liste. Drei
 * Symptome hingen daran, und alle drei waren Aussagen über den Bestand, die
 * gar nicht von ihm handelten:
 *
 *   · 646 „Geräte" namens KUCA_0001_MATCH — das ist der Name der Verknüpfung
 *     auf das Küchenmodell, den der Seed-Erzeuger in das Namensfeld
 *     geschrieben hat. Kein Gerät heisst so.
 *   · „446 ohne Raumzuordnung" — ausnahmslos Altzeilen. Von den 6.674
 *     Modellbauteilen trägt JEDES einen Raum.
 *   · Alle 7.519 auf SEED_NOT_SYNCED — ein Zustand, der „aus PDF/Excel
 *     eingelesen" bedeutet, an Bauteilen, die aus dem Modell gelesen wurden.
 *
 * Deshalb steht die Zahl der Planpositionen jetzt neben dem Bestand und nie
 * mehr in ihm. Ausgeblendet werden sie nicht — 646 Positionen ohne
 * Gerätezuordnung sind offene Arbeit, und das soll man sehen.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE DATENBANK IST FÜHREND
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Diese Seite fragt Revit NICHT. Sie zeigt den Datenbankstand, und was am
 * Modell beobachtet wurde, steht getrennt daneben („Modell") — nie vermischt.
 * Eine Änderung geht zuerst in die Datenbank, erhöht die Revision und setzt
 * den Zustand auf DB_DIRTY; nach Revit kommt sie erst mit einem
 * Schreibvorgang.
 */

interface Rahmen { api: ApiClient; projekt?: { id: string } }

interface Auswahl {
  schluessel: string;
  titel: string;
  filter: {
    etage?: string;
    abschnittId?: string;
    raumId?: string;
    ohneRaum?: boolean;
  };
}

const STATUSFARBE: Record<string, string> = {
  IN_SYNC: 'var(--gut)',
  DB_DIRTY: 'var(--warnung)',
  REVIT_DRIFT: 'var(--warnung)',
  CONFLICT: 'var(--schlecht)',

  // Offene Arbeit, kein Fehler: 6.674 inventarisierte Bauteile warten auf
  // ihren ersten Marker. Rot für den gesamten Bestand wäre dasselbe Rauschen,
  // in dem SEED_NOT_SYNCED zwei Tage lang unbemerkt stand.
  MARKER_MISSING: 'var(--warnung)',

  KUCA_UNMATCHED: 'var(--schlecht)',
  REVIEW_REQUIRED: 'var(--warnung)',
  ERROR: 'var(--schlecht)',
  SEED_NOT_SYNCED: 'var(--text-schwach)',
  INACTIVE: 'var(--text-sehr-schwach)',
};

const STATUSTEXT: Record<string, string> = {
  IN_SYNC: 'Datenbank und Modell stimmen überein',
  DB_DIRTY: 'Datenbank geändert, Modell noch nicht nachgezogen',
  REVIT_DRIFT: 'Modell hat sich bewegt, die Datenbank nicht',
  CONFLICT: 'beide geändert — braucht eine Entscheidung',
  MARKER_MISSING: 'im Modell inventarisiert, Marker noch zu schreiben',
  KUCA_UNMATCHED: 'kein Küchenbauteil zugeordnet',
  REVIEW_REQUIRED: 'Zuordnung prüfen',
  ERROR: 'technischer Fehler',
  SEED_NOT_SYNCED: 'aus der Planauswertung eingelesen, kein Modellbezug',
  INACTIVE: 'fachlich entfallen',
};

/**
 * Woher die angezeigte Gerätebezeichnung kommt.
 *
 * Der Unterschied ist nicht kosmetisch: Eine über die Positionsnummer
 * aufgelöste Bezeichnung ist ein Befund aus dem Modell, keine gepflegte
 * Angabe. Wer sie nicht unterscheiden kann, hält einen Vorschlag für eine
 * Festlegung.
 */
const BEZEICHNUNGSHERKUNFT: Record<string, string> = {
  modell_typ: 'Typname des Küchenbauteils',
  modell_familie: 'Familienname — der Typ benennt nichts ('
                + '„Norm", „Standard" oder eine reine Artikelnummer)',
  planauswertung: 'aus der Planauswertung oder von Hand gepflegt',
  modell_position: 'über die Positionsnummer aus dem Modell aufgelöst — '
                 + 'genau ein Bauteil trägt diese Nummer',
  modell_position_mehrdeutig: 'mehrere Modellbauteile tragen diese '
                            + 'Positionsnummer — die Zuordnung steht aus',
  ohne: 'keine Bezeichnung vorhanden',
};

const VERMERKFARBE: Record<string, string> = {
  high: 'var(--gut)',
  medium: 'var(--warnung)',
  low: 'var(--schlecht)',
  pruefen: 'var(--schlecht)',
};

const GEWERKSYMBOL: Record<string, React.ReactNode> = {
  Elektro: <IconElektro size={12} />,
  'Sanitär': <IconSanitaer size={12} />,
  'Lüftung': <IconLueftung size={12} />,
};

export function Kuechenplanung() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [uebersicht, setUebersicht] = useState<any | null>(null);
  const [baum, setBaum] = useState<any[] | null>(null);
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [suche, setSuche] = useState('');
  const [gewaehlt, setGewaehlt] = useState<Auswahl | null>(null);
  const [ansicht, setAnsicht] = useState<
    'baum' | 'offen' | 'planpositionen' | 'raeume' | 'plaene'>('baum');
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = async () => {
    if (!projekt) return;

    try {
      const [u, b] = await Promise.all([
        api.kuecheUebersicht(projekt.id),
        api.kuecheBaum(projekt.id),
      ]);

      setUebersicht(u);
      setBaum(b.baum ?? []);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { laden(); }, [api, projekt?.id]);

  // Die Suche filtert im Browser. Der Baum trägt nur Struktur und Zahlen;
  // eine Abfrage je Tastendruck wäre verschwendet.
  const gefiltert = useMemo(() => {
    if (!baum) return null;
    if (!suche.trim()) return baum;

    const t = suche.trim().toLowerCase();
    const passt = (s?: string | null) => (s ?? '').toLowerCase().includes(t);

    return baum
      .map((e: any) => ({
        ...e,
        abschnitte: (e.abschnitte ?? [])
          .map((a: any) => ({
            ...a,
            raeume: (a.raeume ?? []).filter(
              (r: any) => passt(r.name) || passt(r.raumnummer)),
          }))
          .filter((a: any) => passt(a.name) || a.raeume.length > 0),

        // Auch die Räume ohne Abschnitt — sie sind am Bestand die Regel und
        // nicht der Sonderfall, und eine Suche, die sie übergeht, findet
        // nichts.
        raeume: (e.raeume ?? []).filter(
          (r: any) => passt(r.name) || passt(r.raumnummer)),
      }))
      .filter((e: any) => passt(e.etage)
                       || e.abschnitte.length > 0
                       || e.raeume.length > 0);
  }, [baum, suche]);

  if (!projekt) return <LoadingState text="Projekt wird geladen …" zeilen={6} spalten={3} />;

  if (fehler) {
    return (
      <>
        <PageHeader titel="Küchenplanung" beschreibung={KOPFTEXT} />
        <ErrorState
          meldung="Der Küchenbestand konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      </>
    );
  }

  if (!uebersicht) {
    return (
      <>
        <PageHeader titel="Küchenplanung" beschreibung={KOPFTEXT} />
        <LoadingState text="Küchenbestand wird geladen …" zeilen={8} spalten={4} />
      </>
    );
  }

  if (uebersicht.gesamt === 0) {
    return (
      <>
        <PageHeader titel="Küchenplanung" beschreibung={KOPFTEXT} />
        <EmptyState
          titel="Noch kein Küchenbestand."
          erklaerung={'Die Auswertung der 31 Küchenplan-PDFs wird mit dem Werkzeug des '
            + 'Workers eingelesen: `kueche-paket-einlesen <projektId> deploy/kueche '
            + '--schreiben`. Ohne `--schreiben` ist es ein Probelauf: Er zeigt die '
            + 'Zahlen und übernimmt nichts.'}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        titel="Küchenplanung"
        beschreibung={KOPFTEXT}
        meta={`${uebersicht.gesamt.toLocaleString('de-DE')} Komponenten · `
          + `${uebersicht.raeume.gesamt} Räume · ${uebersicht.plaene.gesamt} Pläne`}
      />

      <Kennzahlen u={uebersicht} />

      <div style={{ display: 'flex', gap: 4, margin: '12px 0' }}>
        {([
          ['baum', 'Baum'],
          ['offen', `Anschlüsse ohne Gerät (${uebersicht.anschluesseOhneGeraet})`],
          ['planpositionen',
           `Planpositionen (${uebersicht.planpositionen?.gesamt ?? 0})`],
          ['raeume', `Räume (${uebersicht.raeume.gesamt})`],
          ['plaene', `Pläne (${uebersicht.plaene.gesamt})`],
        ] as const).map(([wert, text]) => (
          <button
            key={wert}
            type="button"
            className={ansicht === wert ? 'm-knopf m-knopf-haupt' : 'm-knopf'}
            onClick={() => setAnsicht(wert)}
          >
            {text}
          </button>
        ))}
      </div>

      {ansicht === 'offen' && <OffeneAnschluesse api={api} projektId={projekt.id} />}
      {ansicht === 'planpositionen' && (
        <Planpositionen api={api} projektId={projekt.id}
                        u={uebersicht.planpositionen ?? {}}
                        nachAenderung={laden} />
      )}
      {ansicht === 'raeume' && <Raumliste api={api} projektId={projekt.id} />}
      {ansicht === 'plaene' && <Planliste api={api} projektId={projekt.id} />}

      {ansicht === 'baum' && (
        <div className="karte" style={{ display: 'flex', gap: 0, padding: 0 }}>
          {/* Links die Gliederung */}
          <div className="baum">
            <div style={{ padding: '10px 12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <IconSuche size={14} />
                <input
                  value={suche}
                  onChange={(e) => setSuche(e.target.value)}
                  placeholder="Abschnitt oder Raum"
                  style={{ width: '100%', padding: '4px 6px', fontSize: 13 }}
                />
              </label>
            </div>

            {(gefiltert ?? []).map((e: any) => (
              <div key={e.etage}>
                {/* Stufe 1: die Ebene, mit ihrem Revit-Level */}
                <button
                  className="baum-ebene"
                  onClick={() => umschalten(setOffen, e.etage)}
                >
                  <IconEbene size={14} />
                  <span>
                    {e.etage}
                    <span className="schwach" style={{ marginLeft: 5 }}>
                      {e.revitLevel}
                    </span>
                  </span>
                  <Zaehlerband z={e.zaehler} />
                </button>

                {offen.has(e.etage) && (e.abschnitte ?? []).map((a: any) => (
                  <div key={a.id}>
                    {/* Stufe 2: der Abschnitt */}
                    <button
                      className="baum-ebene"
                      style={{ paddingLeft: 26 }}
                      onClick={() => umschalten(setOffen, a.id)}
                    >
                      <IconEbene size={12} />
                      <span style={{ minWidth: 0, overflow: 'hidden',
                                     textOverflow: 'ellipsis',
                                     whiteSpace: 'nowrap' }}>
                        {a.name}
                      </span>
                      <Zaehlerband z={a.zaehler} />
                    </button>

                    {offen.has(a.id) && (
                      <>
                        {/* Stufe 3: die Räume */}
                        {(a.raeume ?? []).map((r: any) => (
                          <button
                            key={r.id}
                            className={`baum-zeile ${
                              gewaehlt?.schluessel === r.id ? 'aktiv' : ''}`}
                            style={{ paddingLeft: 42 }}
                            onClick={() => setGewaehlt({
                              schluessel: r.id,
                              titel: `${e.etage} · ${a.name} · ${r.name}`,
                              filter: { raumId: r.id },
                            })}
                            title={raumTitel(r)}
                          >
                            <IconRaum size={13} />
                            <span style={{ minWidth: 0, overflow: 'hidden',
                                           textOverflow: 'ellipsis',
                                           whiteSpace: 'nowrap' }}>
                              {r.name}
                              {r.raumnummer && (
                                <span className="schwach"
                                      style={{ marginLeft: 5 }}>
                                  {r.raumnummer}
                                </span>
                              )}
                            </span>
                            <Raumvermerk r={r} />
                          </button>
                        ))}

                        {/* Stufe 3b: „Ohne Raumzuordnung" — kein Sonderfall,
                            sondern der größte Knoten. */}
                        {a.ohneRaum && (
                          <button
                            className={`baum-zeile ${
                              gewaehlt?.schluessel === `${a.id}-ohne`
                                ? 'aktiv' : ''}`}
                            style={{ paddingLeft: 42, fontStyle: 'italic' }}
                            onClick={() => setGewaehlt({
                              schluessel: `${a.id}-ohne`,
                              titel: `${e.etage} · ${a.name} · ohne Raumzuordnung`,
                              filter: { abschnittId: a.id, ohneRaum: true },
                            })}
                            title={'Diese Komponenten tragen keine '
                                 + 'Raumzuordnung. Sie kommt aus der Geometrie '
                                 + 'beim Modell-Lesen.'}
                          >
                            <IconWarnung size={12} />
                            <span>ohne Raumzuordnung</span>
                            <Zaehlerband z={a.ohneRaum} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                ))}

                {/*
                  Stufe 2b: die Räume, die direkt an der Ebene hängen.

                  Keines der 6.674 Modellbauteile hat einen Abschnitt — das
                  Feld kommt aus dem Planbereich der PDF-Auswertung, und das
                  Küchenmodell führt es nicht. Ein Baum, der nur über
                  Abschnitte läuft, zeigte deshalb 0 Räume, obwohl jedes
                  Bauteil einem Raum zugeordnet ist.

                  Die vierte Stufe ist damit optional statt erfunden: Wo es
                  Abschnitte gibt, steht Ebene > Abschnitt > Raum; wo nicht,
                  steht Ebene > Raum.
                */}
                {offen.has(e.etage) && (e.raeume ?? []).map((r: any) => (
                  <button
                    key={r.id}
                    className={`baum-zeile ${
                      gewaehlt?.schluessel === r.id ? 'aktiv' : ''}`}
                    style={{ paddingLeft: 26 }}
                    onClick={() => setGewaehlt({
                      schluessel: r.id,
                      titel: `${e.etage} · ${r.name}`,
                      filter: { raumId: r.id },
                    })}
                    title={raumTitel(r)}
                  >
                    <IconRaum size={13} />
                    <span style={{ minWidth: 0, overflow: 'hidden',
                                   textOverflow: 'ellipsis',
                                   whiteSpace: 'nowrap' }}>
                      {r.name}
                      {r.raumnummer && (
                        <span className="schwach" style={{ marginLeft: 5 }}>
                          {r.raumnummer}
                        </span>
                      )}
                    </span>
                    <Raumvermerk r={r} />
                  </button>
                ))}

                {offen.has(e.etage) && e.ohneRaum && (
                  <button
                    className={`baum-zeile ${
                      gewaehlt?.schluessel === `${e.etage}-ohne` ? 'aktiv' : ''}`}
                    style={{ paddingLeft: 26, fontStyle: 'italic' }}
                    onClick={() => setGewaehlt({
                      schluessel: `${e.etage}-ohne`,
                      titel: `${e.etage} · ohne Raumzuordnung`,
                      filter: { etage: e.etage, ohneRaum: true },
                    })}
                    title={'Diese Bauteile tragen keine Raumzuordnung. Sie '
                         + 'kommt aus der Geometrie beim Modell-Lesen.'}
                  >
                    <IconWarnung size={12} />
                    <span>ohne Raumzuordnung</span>
                    <Zaehlerband z={e.ohneRaum} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Rechts die Liste */}
          <div style={{ flex: 1, padding: '14px 16px', minWidth: 0,
                        maxHeight: '70vh', overflowY: 'auto' }}>
            {!gewaehlt ? (
              <div className="leer">
                Links einen Raum wählen — oder „ohne Raumzuordnung".
              </div>
            ) : (
              <Komponentenliste
                api={api}
                projektId={projekt.id}
                auswahl={gewaehlt}
                nachAenderung={laden}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

function umschalten(
  setzen: React.Dispatch<React.SetStateAction<Set<string>>>, wert: string,
) {
  setzen((s) => {
    const neu = new Set(s);
    if (neu.has(wert)) neu.delete(wert); else neu.add(wert);
    return neu;
  });
}

function raumTitel(r: any) {
  const teile = [r.name];
  if (r.raumnummer) teile.push(`Raumbuch ${r.raumnummer}`);
  if (r.herkunft === 'pdf_raumblatt') teile.push('aus dem Blatt Raum_Zuordnung');
  if (r.raumbuchZustand === 'mehrdeutig')
    teile.push(`${r.raumbuchKandidaten} Kandidaten im Raumbuch`);
  if (r.raumbuchZustand === 'vorschlag') teile.push('nur Wortdeckung');
  if (r.raumbuchZustand === 'kein_treffer') teile.push('kein Treffer im Raumbuch');
  return teile.join(' — ');
}

/** Der Vermerk am Raum: eine Zuordnung ist nicht dasselbe wie eine geprüfte. */
function Raumvermerk({ r }: { r: any }) {
  return (
    <span style={{ display: 'flex', gap: 4, marginLeft: 'auto', fontSize: 10,
                   alignItems: 'center' }}>
      {r.raumnummer
        ? <IconOk size={11} />
        : r.raumbuchZustand === 'mehrdeutig'
          ? <span style={{ color: 'var(--warnung)' }}
                  title={`${r.raumbuchKandidaten} Kandidaten`}>
              {r.raumbuchKandidaten}?
            </span>
          : r.raumbuchZustand === 'vorschlag'
            ? <span style={{ color: 'var(--warnung)' }}
                    title="nur Wortdeckung — Vorschlag">~</span>
            : <span className="sehr-schwach"
                    title="kein Treffer im Raumbuch">–</span>}

      <span className="schwach">{r.zaehler.anzahl}</span>
    </span>
  );
}

/**
 * Die Zählwerte je Knoten. Nur was vorhanden ist — eine Reihe Nullen sagt
 * nichts und verdeckt das eine, was zählt.
 */
function Zaehlerband({ z }: { z: any }) {
  if (!z) return null;

  return (
    <span style={{ display: 'flex', gap: 4, marginLeft: 'auto', fontSize: 10,
                   alignItems: 'center' }}>
      {z.konflikt > 0 && (
        <span style={{ color: 'var(--schlecht)' }}
              title="Konflikt: Datenbank und Modell beide geändert">
          {z.konflikt}!
        </span>
      )}
      {z.markerFehlt > 0 && (
        <span style={{ color: 'var(--schlecht)' }} title="Marker fehlt">
          {z.markerFehlt}M
        </span>
      )}
      {z.zuPruefen > 0 && (
        <span style={{ color: 'var(--warnung)' }} title="zu prüfen">
          {z.zuPruefen}?
        </span>
      )}
      {z.ohneTechnik > 0 && (
        <span className="sehr-schwach" title="ohne technische Daten">
          {z.ohneTechnik}∅
        </span>
      )}
      {z.inSync > 0 && (
        <span style={{ color: 'var(--gut)' }} title="mit dem Modell gleich">
          {z.inSync}
        </span>
      )}
      <span className="schwach">{z.anzahl}</span>
    </span>
  );
}

/**
 * Die Kennzahlen — die offenen Punkte zuerst, nicht die Gesamtzahl.
 *
 * Jede Zahl hier handelt vom BESTAND, also von den Bauteilen aus den beiden
 * KUCA-Modellen. Die Planpositionen der Altauswertung haben ihre eigene Zahl
 * und ihren eigenen Reiter — vermischt haben sie vorher drei Aussagen über den
 * Bestand verfälscht.
 */
function Kennzahlen({ u }: { u: any }) {
  const p = u.planpositionen ?? {};

  return (
    <div className="kennzahlen">
      <Zahl wert={u.gesamt} text="Bauteile im Modell"
            hinweis={`${u.aktiv} aktiv · aus den beiden KUCA-Verknüpfungen`} />
      <Zahl wert={u.ohneTechnik} text="ohne technische Daten"
            farbe={u.ohneTechnik > 0 ? 'var(--warnung)' : undefined}
            hinweis="im Modell vorhanden, HKLSE-Anschlüsse noch nicht zugeordnet" />
      <Zahl wert={u.anschluesseOhneGeraet} text="Anschlüsse ohne Gerät"
            farbe={u.anschluesseOhneGeraet > 0 ? 'var(--warnung)' : undefined}
            hinweis={`von ${u.anschluesse} Punkten insgesamt`} />
      <Zahl wert={u.ohneRaum} text="ohne Raumzuordnung"
            farbe={u.ohneRaum > 0 ? 'var(--warnung)' : undefined}
            hinweis="kommt aus der Geometrie beim Modell-Lesen" />
      <Zahl wert={p.gesamt ?? 0} text="Planpositionen (kein Bestand)"
            hinweis={`${p.ohneGeraet ?? 0} ohne Gerätezuordnung · `
                   + `${p.ohneRaum ?? 0} ohne Raum · eigener Reiter`} />
      <Zahl wert={u.raeume.mitNummer} text="Räume mit Raumbuchnummer"
            hinweis={`von ${u.raeume.gesamt} · ${u.raeume.mehrdeutig} mehrdeutig, `
                   + `${u.raeume.ohneTreffer} ohne Treffer`} />
      <Zahl wert={u.plaene.gesamt} text="Pläne"
            hinweis={`${u.plaene.technischeCallouts} technische Beschriftungen`} />
    </div>
  );
}

function Zahl({ wert, text, hinweis, farbe }: {
  wert: number; text: string; hinweis?: string; farbe?: string;
}) {
  return (
    <div title={hinweis}>
      <div className="kennzahl-wert" style={farbe ? { color: farbe } : undefined}>
        {wert.toLocaleString('de-DE')}
      </div>
      <div className="kennzahl-name">{text}</div>
      {hinweis && <div className="sehr-schwach"
                       style={{ fontSize: 10 }}>{hinweis}</div>}
    </div>
  );
}

/** Die Komponenten eines Knotens, und beim Aufklappen alles zu einer. */
function Komponentenliste({ api, projektId, auswahl, nachAenderung,
                           herkunft, importStatus }: {
  api: ApiClient; projektId: string; auswahl: Auswahl; nachAenderung: () => void;
  herkunft?: string; importStatus?: string;
}) {
  const [liste, setListe] = useState<any[] | null>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const filter = { ...auswahl.filter, herkunft, importStatus };

  useEffect(() => {
    setListe(null);
    setOffen(null);

    api.kuecheKomponenten(projektId, filter)
      .then((a) => setListe(a.komponenten ?? []))
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId, auswahl.schluessel, herkunft, importStatus]);

  if (fehler) return <div className="hinweis fehler">{fehler}</div>;
  if (!liste) return <div className="leer">wird geladen …</div>;

  return (
    <>
      <h3 style={{ margin: '0 0 4px' }}>{auswahl.titel}</h3>
      <p className="schwach" style={{ margin: '0 0 12px', fontSize: 12 }}>
        {liste.length} Komponenten ·{' '}
        {liste.filter((k) => k.punktanzahl === 0).length} ohne technische Daten
      </p>

      <table>
        <thead>
          <tr>
            <th>Position</th>
            <th>Gerät</th>
            <th style={{ textAlign: 'right' }}>Punkte</th>
            <th>Gewerke</th>
            <th style={{ textAlign: 'right' }}>kW</th>
            <th style={{ textAlign: 'right' }}>m³/h</th>
            <th>Zustand</th>
          </tr>
        </thead>
        <tbody>
          {liste.map((k) => (
            <Fragment key={k.id}>
              <tr
                onClick={() => setOffen(offen === k.id ? null : k.id)}
                style={{ cursor: 'pointer',
                         background: offen === k.id ? 'var(--flaeche)' : undefined }}
              >
                <td style={{ whiteSpace: 'nowrap' }}>
                  {k.positionId ?? (
                    <span className="sehr-schwach" title={k.geraeteKey}>
                      aus KUCA
                    </span>
                  )}
                </td>
                <td><Geraetename k={k} /></td>
                <td style={{ textAlign: 'right' }}>
                  {k.punktanzahl === 0
                    ? <span className="sehr-schwach" title="keine Anschlussdaten
                             zugeordnet">0</span>
                    : k.punktanzahl}
                </td>
                <td style={{ fontSize: 11 }}>{k.gewerke ?? ''}</td>
                <td style={{ textAlign: 'right' }}>
                  {k.elektroKw != null ? Number(k.elektroKw).toLocaleString(
                    'de-DE', { maximumFractionDigits: 2 }) : ''}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {k.luftmengeM3h != null ? Number(k.luftmengeM3h)
                    .toLocaleString('de-DE', { maximumFractionDigits: 0 }) : ''}
                </td>
                <td>
                  <Statuszeichen status={k.syncStatus} />
                </td>
              </tr>

              {offen === k.id && (
                <tr>
                  <td colSpan={7} style={{ padding: 0 }}>
                    <Komponente
                      api={api}
                      projektId={projektId}
                      id={k.id}
                      nachAenderung={() => {
                        api.kuecheKomponenten(projektId, filter)
                          .then((a) => setListe(a.komponenten ?? []));
                        nachAenderung();
                      }}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
}

/**
 * Der Gerätename in einer Listenzeile — und was an seiner Stelle steht, wenn
 * es keinen gibt.
 *
 * Ein Gedankenstrich für „kein Name" und ein Gedankenstrich für „Name über die
 * Positionsnummer aufgelöst" wären dieselbe Anzeige für zwei verschiedene
 * Stände. Deshalb tragen die abgeleiteten Fälle ein Zeichen: `~` für aus dem
 * Modell aufgelöst, `?` für mehrdeutig.
 */
function Geraetename({ k }: { k: any }) {
  const h = k.bezeichnungHerkunft as string | undefined;
  const hinweis = h ? BEZEICHNUNGSHERKUNFT[h] : undefined;

  if (!k.bezeichnung) {
    return (
      <span className="sehr-schwach"
            title="Planposition ohne Gegenstück im Modell — es gibt keine
                   Gerätebezeichnung, und es wird keine erfunden.">
        ohne Gerätezuordnung
      </span>
    );
  }

  if (h === 'modell_position_mehrdeutig') {
    return (
      <span style={{ color: 'var(--warnung)' }} title={hinweis}>
        {k.bezeichnung} <span className="sehr-schwach">?</span>
      </span>
    );
  }

  if (h === 'modell_position') {
    return (
      <span title={hinweis}>
        {k.bezeichnung}{' '}
        <span className="sehr-schwach" title={hinweis}>~</span>
      </span>
    );
  }

  return <span title={hinweis}>{k.bezeichnung}</span>;
}

function Statuszeichen({ status }: { status: string }) {
  return (
    <span style={{ color: STATUSFARBE[status] ?? 'var(--text-schwach)',
                   fontSize: 11 }}
          title={STATUSTEXT[status] ?? status}>
      {status === 'IN_SYNC' && <IconOk size={11} />}
      {(status === 'CONFLICT' || status === 'ERROR') && <IconWarnung size={11} />}
      <span style={{ marginLeft: 4 }}>{status}</span>
    </span>
  );
}

/**
 * Eine Komponente in fünf Bereichen: Übersicht, TGA-Daten, Modell, Quelle,
 * Historie. Die Reihenfolge ist die der Anforderung.
 */
function Komponente({ api, projektId, id, nachAenderung }: {
  api: ApiClient; projektId: string; id: string; nachAenderung: () => void;
}) {
  const [d, setD] = useState<any | null>(null);
  const [reiter, setReiter] = useState<
    'uebersicht' | 'tga' | 'modell' | 'quelle' | 'historie'>('tga');
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = () => api.kuecheKomponente(projektId, id)
    .then(setD)
    .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));

  useEffect(() => { laden(); }, [api, projektId, id]);

  if (fehler) return <div className="hinweis fehler">{fehler}</div>;
  if (!d) return <div className="leer">wird geladen …</div>;

  return (
    <div style={{ padding: '12px 14px', background: 'var(--flaeche)',
                  borderTop: '1px solid var(--linie)' }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
        {([
          ['uebersicht', 'Übersicht'],
          ['tga', `TGA-Daten (${d.tga.punktanzahl})`],
          ['modell', 'Modell'],
          ['quelle', 'Quelle'],
          ['historie', `Historie (${d.historie.length})`],
        ] as const).map(([wert, text]) => (
          <button
            key={wert}
            className={reiter === wert ? 'betont' : undefined}
            onClick={() => setReiter(wert)}
            style={{ padding: '3px 8px', fontSize: 11 }}
          >
            {text}
          </button>
        ))}
      </div>

      {reiter === 'uebersicht' && (
        <Uebersichtsbereich
          api={api} projektId={projektId} d={d}
          nachAenderung={() => { laden(); nachAenderung(); }}
        />
      )}
      {reiter === 'tga' && <TgaBereich d={d} />}
      {reiter === 'modell' && <ModellBereich d={d} />}
      {reiter === 'quelle' && <QuelleBereich d={d} />}
      {reiter === 'historie' && <HistorieBereich d={d} />}
    </div>
  );
}

function Uebersichtsbereich({ api, projektId, d, nachAenderung }: {
  api: ApiClient; projektId: string; d: any; nachAenderung: () => void;
}) {
  const u = d.uebersicht;
  const [geraet, setGeraet] = useState<string>(u.geraet ?? '');
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  const speichern = async () => {
    setLaeuft(true);
    setMeldung(null);

    try {
      await api.kuecheKomponenteAendern(projektId, u.id, {
        dbRevision: u.dbRevision,
        geraet,
        begruendung: 'Im Frontend geändert.',
      });

      setMeldung('gespeichert — der Zustand steht jetzt auf DB_DIRTY');
      nachAenderung();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <>
      <dl className="feldliste">
        <Feld name="Geräteschlüssel" wert={u.geraeteKey} />
        <Feld name="Position" wert={u.positionId ?? '— (aus KUCA entdeckt)'} />
        <Feld name="Ebene" wert={`${u.etage} · ${u.revitLevel}`} />
        <Feld name="Abschnitt" wert={u.abschnitt ?? '—'} />
        <Feld name="Raum"
              wert={u.raum
                ? `${u.raum}${u.raumnummer ? ` · ${u.raumnummer}` : ''}`
                : 'ohne Raumzuordnung'} />
        {u.raumQuelle && (
          <Feld name="Raum kommt aus"
                wert={`${{
                  seedblatt: 'Seedblatt der Auswertung',
                  raumblatt: 'Blatt Raum_Zuordnung',
                  revit: 'Modell',
                  gepflegt: 'von Hand bestätigt',
                }[u.raumQuelle as string] ?? u.raumQuelle}`}
                zusatz={u.raumVermerk
                  ? `Sicherheit ${u.raumVermerk}` : undefined}
                farbe={VERMERKFARBE[u.raumVermerk]} />
        )}
        {u.raumRoh && u.raumRoh !== u.raum && (
          <Feld name="Raumname im Plan" wert={u.raumRoh}
                zusatz="vor der Bereinigung" />
        )}
        <Feld name="Zustand" wert={u.syncStatus}
              zusatz={STATUSTEXT[u.syncStatus]}
              farbe={STATUSFARBE[u.syncStatus]} />
        <Feld name="DB-Revision"
              wert={`${u.dbRevision}`}
              zusatz={u.geschriebeneRevision != null
                ? `zuletzt nach Revit geschrieben: ${u.geschriebeneRevision}`
                : 'noch nie nach Revit geschrieben'} />
        {/*
          BEFUND 20.08.2026: Hier stand für alles ausser 'KUCA_RUNTIME' der
          Text 'Planauswertung' — also auch für die 6.674 Bauteile, die aus
          dem Küchenmodell gelesen wurden. Genau verkehrt, und der Wert
          'KUCA_RUNTIME' existiert seit Migration 0401 gar nicht mehr.
        */}
        <Feld name="Herkunft"
              wert={{
                MODELL: 'aus dem Küchenmodell inventarisiert',
                LEGACY_PREMODEL_SEED: 'Planauswertung vor dem Modellinventar',
                KUCA_RUNTIME: 'im Küchenmodell entdeckt',
              }[u.herkunft as string] ?? u.herkunft}
              zusatz={u.herkunft === 'LEGACY_PREMODEL_SEED'
                ? 'kein Bestand — die Bauteile kommen aus dem Modell'
                : undefined} />
        <Feld name="Prüfstand der Extraktion"
              wert={{
                PLAN_POSITION_MODELL_EINDEUTIG:
                  'Planposition · genau ein Modellbauteil trägt diese Nummer',
                PLAN_POSITION_MODELL_MEHRDEUTIG:
                  'Planposition · mehrere Modellbauteile tragen diese Nummer',
                PLAN_POSITION_OHNE_MODELLBEZUG:
                  'Planposition · kein Modellbauteil trägt diese Nummer',
              }[u.importStatus as string] ?? u.importStatus ?? '—'} />

        {/*
          Der Beleg der angezeigten Bezeichnung. Bei einem Modellbauteil sind
          Familie und Typ die ganze Begründung; bei einer Planposition ist es
          der Modellbestand an derselben Nummer.
        */}
        {(u.kucaFamilie || u.kucaTyp) && (
          <Feld name="Familie · Typ im Modell"
                wert={[u.kucaFamilie, u.kucaTyp].filter(Boolean).join(' · ')} />
        )}
        {u.bezeichnung && (
          <Feld name="Angezeigte Bezeichnung" wert={u.bezeichnung}
                zusatz={BEZEICHNUNGSHERKUNFT[u.bezeichnungHerkunft as string]}
                farbe={u.bezeichnungHerkunft === 'modell_position_mehrdeutig'
                  ? 'var(--warnung)' : undefined} />
        )}
        {(u.modellgeraete?.length ?? 0) > 0 && (
          <Feld name={`Modellbauteile an Position ${u.positionId}`}
                wert={u.modellgeraete.join(', ')}
                zusatz={u.modellgeraete.length === 1
                  ? 'eindeutig — daraus stammt die angezeigte Bezeichnung'
                  : `${u.modellgeraete.length} Bauteile — die Zuordnung steht `
                  + 'aus und wird nicht geraten'} />
        )}
      </dl>

      <div style={{ marginTop: 12, display: 'flex', gap: 8,
                    alignItems: 'flex-end' }}>
        <label style={{ flex: 1 }}>
          <span className="schwach" style={{ fontSize: 11 }}>Gerätebezeichnung</span>
          <input
            value={geraet}
            onChange={(e) => setGeraet(e.target.value)}
            style={{ width: '100%', padding: '4px 6px', fontSize: 13 }}
          />
        </label>
        <button
          onClick={speichern}
          disabled={laeuft || geraet === (u.geraet ?? '')}
          className="betont"
          style={{ padding: '5px 12px' }}
        >
          {laeuft ? 'speichert …' : 'Speichern'}
        </button>
      </div>

      {meldung && (
        <p className="schwach" style={{ fontSize: 12, marginTop: 6 }}>
          {meldung}
        </p>
      )}

      <p className="sehr-schwach" style={{ fontSize: 11, marginTop: 8 }}>
        Eine Änderung geht zuerst in die Datenbank und erhöht die Revision.
        Nach Revit kommt sie erst mit einem Schreibvorgang — nichts wird still
        im Modell geändert.
      </p>
    </>
  );
}

function TgaBereich({ d }: { d: any }) {
  const t = d.tga;

  if (t.anschluesse.length === 0) {
    return (
      <div className="hinweis">
        <strong>Keine Anschlussdaten zugeordnet.</strong> Die Positionsnummer
        steht im Plan, die technischen Daten daneben ließen sich ihr nicht
        zuordnen — Prüfstand{' '}
        <code>{d.uebersicht.importStatus}</code>. Das betrifft 646 der 845
        Komponenten und schließt sich erst mit dem Modell-Lesen: Die 610 Punkte
        ohne Gerät werden dann geometrisch zugeordnet.
      </div>
    );
  }

  return (
    <>
      <dl className="feldliste">
        <Feld name="Gewerke" wert={t.gewerke ?? '—'} />
        <Feld name="Elektrische Leistung"
              wert={t.elektroKw != null ? `${t.elektroKw} kW` : '—'} />
        <Feld name="Spannungen" wert={t.spannungenV ?? '—'} />
        <Feld name="Nennweiten" wert={t.sanitaerDn ?? '—'} />
        <Feld name="Luftvolumenstrom"
              wert={t.luftmengeM3h != null ? `${t.luftmengeM3h} m³/h` : '—'} />
        <Feld name="Anschlusshöhen" wert={t.anschlusshoehenMm ?? '—'} />
        <Feld name="Medien" wert={t.medien ?? '—'} />
      </dl>

      <table style={{ marginTop: 10 }}>
        <thead>
          <tr>
            <th>Punkt</th>
            <th>Gewerk</th>
            <th>Anschluss</th>
            <th style={{ textAlign: 'right' }}>Anz.</th>
            <th style={{ textAlign: 'right' }}>V</th>
            <th style={{ textAlign: 'right' }}>kW</th>
            <th>DN</th>
            <th style={{ textAlign: 'right' }}>m³/h</th>
            <th style={{ textAlign: 'right' }}>Höhe</th>
            <th>Bezug</th>
            <th>Medium</th>
          </tr>
        </thead>
        <tbody>
          {t.anschluesse.map((a: any) => (
            <tr key={a.id} title={a.beschreibungRoh ?? undefined}>
              <td style={{ whiteSpace: 'nowrap' }}>
                {GEWERKSYMBOL[a.gewerk] ?? null} {a.punktId}
              </td>
              <td>{a.gewerk ?? ''}</td>
              <td>{a.anschlusstyp ?? ''}</td>
              <td style={{ textAlign: 'right' }}>{a.anzahl ?? ''}</td>
              <td style={{ textAlign: 'right' }}>{a.spannungV ?? ''}</td>
              <td style={{ textAlign: 'right' }}>{a.leistungKwJe ?? ''}</td>
              <td>{a.dn ?? ''}</td>
              <td style={{ textAlign: 'right' }}>{a.luftmengeM3h ?? ''}</td>
              <td style={{ textAlign: 'right' }}>{a.anschlusshoeheMm ?? ''}</td>
              <td style={{ fontSize: 11 }}>{a.hoehenbezug ?? ''}</td>
              <td>{a.medium ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Der Rohtext bleibt sichtbar. Er ist der Nachweis, dass die Zahlen
          darüber nicht hinzugedacht sind. */}
      <details style={{ marginTop: 10 }}>
        <summary className="schwach" style={{ fontSize: 12, cursor: 'pointer' }}>
          Rohtext der Planbeschriftungen
        </summary>
        <ul style={{ fontSize: 11, marginTop: 6 }}>
          {t.anschluesse.filter((a: any) => a.beschreibungRoh).map((a: any) => (
            <li key={`roh-${a.id}`}>
              <code>{a.punktId}</code>: {a.beschreibungRoh}
              {a.pruefhinweis && (
                <div style={{ color: 'var(--warnung)' }}>{a.pruefhinweis}</div>
              )}
            </li>
          ))}
        </ul>
      </details>
    </>
  );
}

function ModellBereich({ d }: { d: any }) {
  const m = d.modell;
  const nieGelesen = m.beobachtetAm == null;

  return (
    <>
      {nieGelesen && (
        <div className="hinweis">
          <strong>Dieses Gerät wurde noch nie im Modell gelesen.</strong> Ein
          leeres Feld heißt hier „nicht gelesen" und nicht „nichts gefunden".
          Der Marker entsteht mit dem ersten Schreibvorgang: 10 cm Kantenlänge,
          Unterkante {m.sollOffsetM.toFixed(2).replace('.', ',')} m über{' '}
          <code>{d.uebersicht.revitLevel}</code>, an der Stelle des
          Küchenbauteils.
        </div>
      )}

      <dl className="feldliste">
        <Feld name="Marker (UniqueId)" wert={m.markerUniqueId ?? '—'} />
        <Feld name="Küchenbauteil (UniqueId)" wert={m.kucaUniqueId ?? '—'} />
        <Feld name="Familie / Typ"
              wert={m.kucaFamilie ? `${m.kucaFamilie} · ${m.kucaTyp}` : '—'} />
        <Feld name="Beobachtete Lage"
              wert={m.beobachtetXM != null
                ? `${m.beobachtetXM.toFixed(3)} / ${m.beobachtetYM.toFixed(3)}`
                  + ` / ${m.beobachtetZM.toFixed(3)} m`
                : '—'} />
        <Feld name="Beobachtetes Level" wert={m.beobachtetLevel ?? '—'} />
        <Feld name="Soll-Unterkante"
              wert={`${d.uebersicht.revitLevel} + `
                  + `${m.sollOffsetM.toFixed(2).replace('.', ',')} m`} />
        <Feld name="Zuordnungsverfahren" wert={m.matchMethode ?? '—'}
              zusatz={m.matchScore != null ? `Score ${m.matchScore}` : undefined} />
        <Feld name="Abstand Suchpunkt zum Bauteil"
              wert={m.pdfKucaAbstandM != null
                ? `${m.pdfKucaAbstandM.toFixed(2)} m` : '—'}
              zusatz="Gegenprobe: die Planbeschriftung liegt neben dem Gerät" />
        <Feld name="Zuletzt gelesen" wert={zeit(m.beobachtetAm)} />
        <Feld name="Zuletzt geschrieben" wert={zeit(m.letzterSyncUtc)} />
      </dl>

      {m.abweichung && (
        <details open style={{ marginTop: 10 }}>
          <summary style={{ color: 'var(--warnung)', fontSize: 12,
                            cursor: 'pointer' }}>
            Abweichungen Datenbank gegen Modell
          </summary>
          <pre style={{ fontSize: 11, overflowX: 'auto' }}>{m.abweichung}</pre>
        </details>
      )}

      {m.beobachtungen.length > 0 && (
        <table style={{ marginTop: 10 }}>
          <thead>
            <tr>
              <th>gelesen</th><th>Marker</th><th>Lage</th><th>Level</th>
              <th style={{ textAlign: 'right' }}>Offset</th><th>Raum</th>
            </tr>
          </thead>
          <tbody>
            {m.beobachtungen.map((b: any) => (
              <tr key={b.id}>
                <td>{zeit(b.beobachtetAm)}</td>
                <td style={{ fontSize: 10 }}>{b.markerUniqueId ?? '—'}</td>
                <td style={{ fontSize: 11 }}>
                  {b.hostXM != null
                    ? `${b.hostXM.toFixed(2)} / ${b.hostYM.toFixed(2)}` : '—'}
                </td>
                <td>{b.levelName ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  {b.offsetUeberLevelM != null
                    ? `${b.offsetUeberLevelM.toFixed(3)} m` : '—'}
                </td>
                <td>{b.raumName ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function QuelleBereich({ d }: { d: any }) {
  const q = d.quelle;

  return (
    <>
      <dl className="feldliste">
        <Feld name="Plan" wert={q.quellePdf ?? '—'} />
        {q.quellePdfAlle && q.quellePdfAlle !== q.quellePdf && (
          <Feld name="Alle Pläne mit diesem Gerät" wert={q.quellePdfAlle} />
        )}
        <Feld name="Planbereich" wert={q.planbereich ?? '—'} />
        <Feld name="Achsschnittpunkte"
              wert={q.achsref1 ? `${q.achsref1}   ·   ${q.achsref2}` : '—'}
              zusatz="zwei benannte Punkte je Plan — Fallback und Gegenprobe" />
        <Feld name="Planblattkoordinate"
              wert={q.sourceXM != null
                ? `${q.sourceXM.toFixed(2)} / ${q.sourceYM.toFixed(2)} m` : '—'}
              zusatz="Suchpunkt, NICHT die Markerposition" />
        <Feld name="Registrierung" wert={q.planRegistrierung ?? '—'} />
        <Feld name="Lokalisierung"
              wert={q.pdfLokalisierung ?? '—'}
              zusatz={q.lokalisierungConfidence
                ? `Sicherheit ${q.lokalisierungConfidence}` : undefined} />
        <Feld name="Hash der Quellmappe" wert={q.quellHash ?? '—'} />
        <Feld name="Hash des Datenbankstands" wert={q.datenHash ?? '—'}
              zusatz="entscheidet, ob nach Revit geschrieben werden muss" />
      </dl>

      {q.plan && (
        <dl className="feldliste" style={{ marginTop: 10 }}>
          <Feld name="Blatt" wert={`${q.plan.blatt ?? '—'} · 1:${q.plan.massstab}`} />
          <Feld name="Registrierungsgüte"
                wert={`${q.plan.siftInlier ?? '—'} Inlier, Medianfehler `
                    + `${q.plan.siftMedianfehlerPx ?? '—'} px`} />
          <Feld name="Referenzabstand"
                wert={q.plan.referenzBasislaengeM != null
                  ? `${q.plan.referenzBasislaengeM.toFixed(1)} m` : '—'}
                zusatz="zwei Punkte weit auseinander bestimmen den Winkel gut" />
          <Feld name="Technische Beschriftungen auf dem Blatt"
                wert={`${q.plan.technischeCallouts ?? '—'}`} />
          {q.plan.hinweis && <Feld name="Hinweis" wert={q.plan.hinweis} />}
        </dl>
      )}

      {q.rohbeschriftungen?.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary className="schwach" style={{ fontSize: 12, cursor: 'pointer' }}>
            Rohbeschriftungen aus dem Plan ({q.rohbeschriftungen.length})
          </summary>
          <ul style={{ fontSize: 11, marginTop: 6 }}>
            {q.rohbeschriftungen.map((c: any, i: number) => (
              <li key={i}>
                <code>{c.calloutId}</code>
                {c.textstatus === 'PRUEFEN' && (
                  <span style={{ color: 'var(--warnung)', marginLeft: 4 }}>
                    (Text nicht sauber dekodierbar)
                  </span>
                )}
                : {c.rohtext}
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}

function HistorieBereich({ d }: { d: any }) {
  if (d.historie.length === 0)
    return <div className="leer">Noch keine Änderung.</div>;

  return (
    <table>
      <thead>
        <tr>
          <th>Zeitpunkt</th><th>Aktion</th><th>Benutzer</th>
          <th>Revision</th><th>Begründung</th>
        </tr>
      </thead>
      <tbody>
        {d.historie.map((h: any) => (
          <tr key={h.id}>
            <td style={{ whiteSpace: 'nowrap' }}>{zeit(h.zeitpunkt)}</td>
            <td>{h.aktion}</td>
            <td>{h.benutzer ?? '—'}</td>
            <td>
              {h.revisionVorher != null ? `${h.revisionVorher} → ` : ''}
              {h.revisionNachher ?? '—'}
            </td>
            <td style={{ fontSize: 11 }}>{h.begruendung ?? ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Die Planpositionen der Altauswertung — kein Bestand, aber offene Arbeit.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM SIE EINEN EIGENEN REITER HABEN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Diese 845 Zeilen sind entstanden, bevor je ein Küchenmodell gelesen wurde:
 * eine Zeile je Positionsnummer auf einem KUER-Plan. Migration 0401 hat sie
 * heruntergestuft, weil ein Gerät nicht deshalb vorhanden ist, weil es auf
 * einem Plan steht.
 *
 * 646 von ihnen fanden in der HKLSE-Liste kein Gerät und trugen deshalb den
 * Platzhalter des Seed-Erzeugers als Namen — 'KUCA_0001_MATCH', den Namen der
 * Verknüpfung auf das Küchenmodell. Sie sind vollständig leer: keine
 * Anschlusspunkte, keine Gewerke, keine technischen Daten. Ihr ganzer Inhalt
 * ist eine Nummer und eine Plankoordinate.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WAS AN IHRE STELLE TRITT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Positionsnummer wird gegen den Modellbestand gehalten. Genau ein
 * Bauteil an derselben Nummer ist eine Auflösung — dort steht die richtige
 * Gerätebezeichnung. Mehrere sind eine Entscheidung, die niemandem abgenommen
 * wird: An 0EG.04.41 stehen 24 Wurzelbauteile, und der erste Treffer wäre
 * gewürfelt. Keines heisst keines — dann bleibt die Zeile ohne Bezeichnung.
 */
function Planpositionen({ api, projektId, u, nachAenderung }: {
  api: ApiClient; projektId: string; u: any; nachAenderung: () => void;
}) {
  const [fall, setFall] = useState('');

  const auswahl: Auswahl = {
    schluessel: `planpositionen:${fall}`,
    titel: fall
      ? ({
          PLAN_POSITION_MODELL_EINDEUTIG:
            'Planpositionen · im Modell eindeutig aufgelöst',
          PLAN_POSITION_MODELL_MEHRDEUTIG:
            'Planpositionen · mehrere Modellbauteile an der Nummer',
          PLAN_POSITION_OHNE_MODELLBEZUG:
            'Planpositionen · kein Modellbauteil an der Nummer',
        }[fall] ?? fall)
      : 'Alle Planpositionen der Altauswertung',
    filter: {},
  };

  return (
    <div className="karte">
      <p style={{ margin: '0 0 10px' }}>
        <strong>{u.gesamt ?? 0} Planpositionen aus der Auswertung vor dem
        Modellinventar.</strong> Sie sind <em>kein</em> Bestand — der Bestand
        sind die Bauteile aus den beiden KUCA-Modellen. {u.mitGeraetedaten ?? 0}
        {' '}von ihnen tragen HKLSE-Anschlussdaten und sind damit die Vorlage
        für die Zuordnung; {u.ohneGeraet ?? 0} sind leer und trugen bis zum
        20.08.2026 den Platzhalter <code>KUCA_0001_MATCH</code> als
        Gerätenamen.
      </p>

      <div style={{ display: 'flex', gap: 4, margin: '0 0 10px',
                    flexWrap: 'wrap' }}>
        {([
          ['', `alle (${u.gesamt ?? 0})`],
          ['PLAN_POSITION_MODELL_EINDEUTIG',
           `im Modell eindeutig (${u.modellEindeutig ?? 0})`],
          ['PLAN_POSITION_MODELL_MEHRDEUTIG',
           `mehrdeutig (${u.modellMehrdeutig ?? 0})`],
          ['PLAN_POSITION_OHNE_MODELLBEZUG',
           `ohne Modellbezug (${u.ohneModellbezug ?? 0})`],
        ] as const).map(([wert, text]) => (
          <button
            key={wert || 'alle'}
            className={fall === wert ? 'betont' : undefined}
            onClick={() => setFall(wert)}
            style={{ padding: '3px 8px', fontSize: 11 }}
          >
            {text}
          </button>
        ))}
      </div>

      <p className="schwach" style={{ margin: '0 0 10px', fontSize: 12 }}>
        <code>~</code> hinter einer Bezeichnung heisst: über die
        Positionsnummer aus dem Modell aufgelöst, nicht gepflegt.
        <code style={{ marginLeft: 6 }}>?</code> heisst: mehrere Modellbauteile
        tragen diese Nummer.
      </p>

      <Komponentenliste
        api={api} projektId={projektId}
        auswahl={auswahl}
        herkunft="LEGACY_PREMODEL_SEED"
        importStatus={fall || undefined}
        nachAenderung={nachAenderung}
      />
    </div>
  );
}

const ANSCHLUSS_SPALTEN: Spalte<any>[] = [
  { schluessel: 'punktId', titel: 'Punkt', breite: '130px', immerSichtbar: true,
    wert: (a) => a.punktId },
  { schluessel: 'etage', titel: 'Ebene', breite: '90px', wert: (a) => a.etage },
  { schluessel: 'gewerk', titel: 'Gewerk', wert: (a) => a.gewerk ?? null },
  { schluessel: 'anschlusstyp', titel: 'Anschluss', wert: (a) => a.anschlusstyp ?? null },
  { schluessel: 'planbereich', titel: 'Planbereich', wert: (a) => a.planbereich ?? null },
  { schluessel: 'beschreibungRoh', titel: 'Rohtext', wert: (a) => a.beschreibungRoh ?? null },
  {
    schluessel: 'pruefhinweis', titel: 'zu tun', wert: (a) => a.pruefhinweis ?? null,
    inhalt: (a) => (a.pruefhinweis
      ? <span style={{ color: 'var(--warnung)' }}>{a.pruefhinweis}</span> : '—'),
  },
];

/**
 * Die Anschlusspunkte ohne Gerät. 610 von 1.066 — sie kommen in keiner
 * Komponentenansicht vor, weil sie zu keiner Komponente gehören, und wären
 * ohne eigene Ansicht unsichtbar.
 */
export function OffeneAnschluesse({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const [d, setD] = useState<any | null>(null);
  const [gewerk, setGewerk] = useState('');
  const [etage, setEtage] = useState('');

  useEffect(() => {
    api.kuecheOffeneAnschluesse(projektId, etage || undefined,
      gewerk || undefined).then(setD);
  }, [api, projektId, etage, gewerk]);

  if (!d) return <LoadingState text="Anschlusspunkte werden geladen …" zeilen={10} spalten={5} />;

  const filter: FilterDefinition[] = [
    {
      schluessel: 'etage', beschriftung: 'Ebene', art: 'auswahl',
      optionen: ['0EG', '1OG', '2OG', '3OG', '4OG', '5OG']
        .map((e) => ({ wert: e, beschriftung: e })),
    },
    {
      schluessel: 'gewerk', beschriftung: 'Gewerk', art: 'auswahl',
      optionen: (d.nachGewerk ?? []).map((g: any) => ({
        wert: g.gewerk, beschriftung: `${g.gewerk} (${g.anzahl})`,
      })),
    },
  ];

  const werte: FilterWerte = { etage, gewerk };

  return (
    <ContentSection
      titel="Anschlusspunkte ohne Gerät"
      beschreibung={'Sie stehen auf einem Küchenplan, tragen aber keine Positionsnummer '
        + '— im 2OG alle 208, weil dort keine Positionsnummern vergeben sind. Verworfen '
        + 'wird keiner: Die Zuordnung entsteht beim Modell-Lesen geometrisch, mit '
        + 'konservativen Grenzen (≤ 0,75 m automatisch, bis 2,00 m zu prüfen, '
        + 'Luftpunkte 2,50 m).'}
      meta={`${d.anzahl} Anschlusspunkte`}
    >
      <FilterBar
        filter={filter}
        werte={werte}
        beiAenderung={(w) => {
          setEtage(String(w.etage ?? ''));
          setGewerk(String(w.gewerk ?? ''));
        }}
        inLeiste={2}
      />

      {/* Umzug U6c: `DataTable` virtualisiert ab 200 Zeilen. Das
          `.slice(0, 400)` mit dem Satz „Es werden die ersten 400 von …"
          entfällt ersatzlos — bis hierher war die größte Bestandsliste
          der ganzen Oberfläche abgeschnitten. */}
      <DataTable
        name="Anschlusspunkte ohne Gerät"
        spalten={ANSCHLUSS_SPALTEN}
        zeilen={d.anschluesse ?? []}
        schluessel={(a) => String(a.id)}
        dicht
        ausfuhr
        ausfuhrDateiname="kueche-anschluesse-ohne-geraet"
        leer={(
          <EmptyState
            titel={etage || gewerk
              ? 'Kein Anschlusspunkt passt zu diesen Filtern.'
              : 'Jeder Anschlusspunkt hängt an einem Gerät.'}
            erklaerung={etage || gewerk
              ? 'Die gesetzten Filter grenzen die Liste auf null Zeilen ein.'
              : 'Es gibt keinen Punkt ohne Positionsnummer — das ist das Ziel.'}
            aktion={etage || gewerk ? (
              <button type="button" className="m-knopf"
                onClick={() => { setEtage(''); setGewerk(''); }}>
                Filter zurücksetzen
              </button>
            ) : undefined}
          />
        )}
      />
    </ContentSection>
  );
}

/**
 * Die Räume und ihr Abgleich gegen das Raumbuch. Hier wird entschieden, was
 * kein Namensvergleich entscheiden kann.
 */
function Raumliste({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [d, setD] = useState<any | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  const laden = () => api.kuecheRaeume(projektId).then(setD);
  useEffect(() => { laden(); }, [api, projektId]);

  const uebernehmen = async (raumId: string, k: any) => {
    setLaeuft(raumId);
    setMeldung(null);

    try {
      await api.kuecheRaumAendern(projektId, raumId, {
        raumNummer: k.nummer,
        revitRaumUniqueId: k.uniqueId,
        begruendung: `Raumbuchkandidat '${k.name}' von Hand bestätigt.`,
      });

      setMeldung(`${k.name} übernommen.`);
      laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  if (!d) return <div className="leer">wird geladen …</div>;

  const rest = d.raeume.filter((r: any) => r.raumbuchZustand === 'kein_treffer');

  return (
    <div className="karte">
      <p style={{ margin: '0 0 10px' }}>
        Die Küchenplanung nennt Räume im Klartext, das Raumbuch kennt sie mit
        Nummer. Wo der Name im Geschoss <strong>eindeutig</strong> ist, wurde
        die Nummer übernommen; wo mehrere passen, entscheidet der Mensch —
        <em> VINYL-CLUB</em> passt auf sieben Räume, und ob der Saal oder die
        Küche darin gemeint ist, sagt kein Namensvergleich.
      </p>

      {meldung && <p className="schwach" style={{ fontSize: 12 }}>{meldung}</p>}

      <table>
        <thead>
          <tr>
            <th>Ebene</th><th>Küchenraum</th>
            <th style={{ textAlign: 'right' }}>Komp.</th>
            <th>Herkunft</th><th>Sicherheit</th><th>Raumbuch</th>
          </tr>
        </thead>
        <tbody>
          {d.raeume.map((r: any) => {
            const kandidaten = r.kandidaten ? JSON.parse(r.kandidaten) : [];

            return (
              <tr key={r.id}>
                <td>{r.etage}</td>
                <td>{r.raumName}</td>
                <td style={{ textAlign: 'right' }}>{r.komponenten}</td>
                <td style={{ fontSize: 11 }}>
                  {{
                    pdf_text: 'Seedblatt',
                    pdf_raumblatt: 'Blatt Raum_Zuordnung',
                    revit: 'Modell',
                    gepflegt: 'bestätigt',
                  }[r.herkunft as string] ?? r.herkunft}
                </td>
                <td>
                  <span style={{ color: VERMERKFARBE[r.pruefstatus],
                                 fontSize: 11 }}>
                    {r.pruefstatus}
                  </span>
                </td>
                <td style={{ fontSize: 11 }}>
                  {r.raumNummer ? (
                    <span style={{ color: 'var(--gut)' }}>
                      <IconOk size={11} /> {r.raumNummer}
                    </span>
                  ) : kandidaten.length > 0 ? (
                    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {kandidaten.map((k: any, i: number) => (
                        <button
                          key={i}
                          onClick={() => uebernehmen(r.id, k)}
                          disabled={laeuft === r.id}
                          title={k.art === 'wortdeckung'
                            ? `nur Wortdeckung ${k.deckung} — Vorschlag`
                            : 'namensgleich'}
                          style={{ fontSize: 10, padding: '1px 5px' }}
                        >
                          {k.name}
                          {k.art === 'wortdeckung' && ` (${k.deckung})`}
                        </button>
                      ))}
                    </span>
                  ) : (
                    <span className="sehr-schwach">kein Treffer</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Die Restliste ausdrücklich am Ende — in einer Tabelle mit 37 Zeilen
          geht sie sonst unter. */}
      {rest.length > 0 && (
        <div className="hinweis" style={{ marginTop: 12 }}>
          <strong>
            Restliste: {rest.length} Räume ohne jeden Treffer im Raumbuch,{' '}
            {rest.reduce((s: number, r: any) => s + r.komponenten, 0)}{' '}
            Komponenten.
          </strong>
          <ul style={{ fontSize: 12, marginTop: 6 }}>
            {rest.map((r: any) => (
              <li key={r.id}>
                {r.etage} · {r.raumName} — {r.komponenten} Komponenten
              </li>
            ))}
          </ul>
          <p className="schwach" style={{ fontSize: 12 }}>
            Über Namen ist diese Zuordnung nicht zu haben. Sie kommt aus der
            Geometrie: Marker setzen, Raum an der Lage bestimmen.
          </p>
        </div>
      )}
    </div>
  );
}

/** Die 31 Pläne und ihre Registrierung — die Grundlage jeder Koordinate. */
export function Planliste({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [d, setD] = useState<any | null>(null);
  useEffect(() => { api.kuechePlaene(projektId).then(setD); }, [api, projektId]);

  if (!d) return <div className="leer">wird geladen …</div>;

  return (
    <div className="karte">
      <p style={{ margin: '0 0 10px' }}>
        Jeder Plan trägt zwei benannte Achsschnittpunkte. Aus ihnen entsteht die
        Transformation vom Planblatt in Revit-Koordinaten — Verschiebung,
        Drehung, ein Maßstab, keine Scherung. Liegt der gemessene Maßstab
        außerhalb 0,98 bis 1,02, wird der Fallback für diesen Plan
        abgeschaltet statt zurechtgebogen.
      </p>

      <table>
        <thead>
          <tr>
            <th>Ebene</th><th>Blatt</th><th>Planbereich</th><th>Maßstab</th>
            <th>Registrierung</th><th style={{ textAlign: 'right' }}>Inlier</th>
            <th style={{ textAlign: 'right' }}>Fehler</th>
            <th>Achsschnittpunkte</th>
            <th style={{ textAlign: 'right' }}>Abstand</th>
            <th style={{ textAlign: 'right' }}>Beschr.</th>
          </tr>
        </thead>
        <tbody>
          {d.plaene.map((p: any) => (
            <tr key={p.id} title={p.hinweis ?? undefined}>
              <td>{p.etage}</td>
              <td>{p.blatt ?? '—'}</td>
              <td style={{ fontSize: 11 }}>{p.planbereich ?? ''}</td>
              <td>1:{p.massstab}</td>
              <td>
                {p.registrierungStatus === 'OK'
                  ? <span style={{ color: 'var(--gut)' }}>OK</span>
                  : p.registrierungStatus}
              </td>
              <td style={{ textAlign: 'right' }}>{p.siftInlier ?? '—'}</td>
              <td style={{ textAlign: 'right' }}>
                {p.siftMedianfehlerPx ?? '—'}
              </td>
              <td style={{ fontSize: 11 }}>
                {p.ref1Achsschnitt} · {p.ref2Achsschnitt}
              </td>
              <td style={{ textAlign: 'right' }}>
                {p.referenzBasislaengeM != null
                  ? `${p.referenzBasislaengeM.toFixed(1)} m` : '—'}
              </td>
              <td style={{ textAlign: 'right' }}>
                {p.technischeCallouts ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Feld({ name, wert, zusatz, farbe }: {
  name: string; wert: string; zusatz?: string; farbe?: string;
}) {
  return (
    <>
      <dt>{name}</dt>
      <dd style={farbe ? { color: farbe } : undefined}>
        {wert}
        {zusatz && (
          <span className="sehr-schwach" style={{ marginLeft: 6, fontSize: 11 }}>
            {zusatz}
          </span>
        )}
      </dd>
    </>
  );
}

function zeit(wert?: string | null) {
  if (!wert) return '—';
  const d = new Date(wert);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('de-DE');
}

/**
 * Fremdmodelle > Küche > Anschlüsse / Pläne / Räume — dünne Routenhüllen
 * (N3: Anschlüsse, Pläne; N5: Räume, der erste der zwei Restposten aus N3).
 *
 * `OffeneAnschluesse`, `Planliste` und `Raumliste` waren bis N3/N5 nur über
 * die internen Reiter „offen", „plaene" und „raeume" dieser Seite
 * erreichbar. Alle drei tragen ihre eigene Datenabfrage
 * (`kuecheOffeneAnschluesse`, `kuechePlaene`, `kuecheRaeume`) und ihre
 * eigene Kopfzeile — anders als „Komponenten" (der Standardbaum, bereits
 * über den Küche-Knoten selbst erreichbar) und „Beobachtungen" (bereits die
 * Zuordnungsseite `Kuechenmapping.tsx`) lassen sie sich deshalb ohne Umbau
 * eigenständig routen. Der Reiter hier bleibt zusätzlich bestehen (kein
 * bestehender Pfad ändert sich). „Technikwerte" bekommt KEINE Route: Es
 * gibt keine Anzeige dafür, weder hier noch anderswo — Navigation verweist
 * nur auf real Vorhandenes (Plan Abschnitt 6).
 *
 * Der ZWEITE Restposten aus N3, „Planpositionen", bekommt bewusst KEINE
 * eigene Route: Der Reiter zeigt `<Komponentenliste .../>` — dieselbe
 * Komponente und denselben Endpunkt (`kuecheKomponenten`), nur mit einem
 * zusätzlichen Filter (`herkunft="LEGACY_PREMODEL_SEED"`), den auch der
 * Baum-Reiter (Stufe 3) schon verwendet. Er trägt keine EIGENE Abfrage,
 * sondern hängt an der geteilten Übersicht — dasselbe Kriterium, das
 * „Komponenten" oben von einer eigenen Route ausnimmt.
 */
export function KuecheAnschluesseSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState text="Projekt wird geladen …" zeilen={6} spalten={5} />;
  return <OffeneAnschluesse api={api} projektId={projekt.id} />;
}

export function KuechePlaeneSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState text="Projekt wird geladen …" zeilen={6} />;

  return (
    <>
      <PageHeader
        titel="Pläne"
        beschreibung={'Jeder Plan trägt zwei benannte Achsschnittpunkte — Verschiebung, '
          + 'Drehung und Maßstab vom Planblatt in Revit-Koordinaten, keine Scherung.'}
      />
      <Planliste api={api} projektId={projekt.id} />
    </>
  );
}

export function KuecheRaeumeSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState text="Projekt wird geladen …" zeilen={6} spalten={5} />;

  return (
    <>
      <PageHeader
        titel="Räume"
        beschreibung={'Der Abgleich der Küchenplanung gegen das Raumbuch: Wo der '
          + 'Raumname im Geschoss eindeutig ist, wurde die Nummer übernommen; wo '
          + 'mehrere passen, entscheidet der Mensch.'}
      />
      <Raumliste api={api} projektId={projekt.id} />
    </>
  );
}
